import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MagnifyingGlass, X, Basket, Sparkle, ForkKnife, Clock, PushPin,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { recipePath } from "@/lib/slug";
import AutoSeo from "@/components/AutoSeo";
import PageMeta from "@/components/PageMeta";
import { toast } from "sonner";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Recipe Creator — a "what can I cook with what's in my fridge?" matcher.
 *
 * User adds ingredients as chips. On submit we POST /api/recipe-creator/match
 * and get back a ranked list of public recipes with match_pct + missing
 * ingredients. Anonymous callers welcome (drives conversion). Feature-flag
 * gated by `recipe_creator_enabled` in Admin → Site Config.
 */
export default function RecipeCreator() {
  const { flags, loading: flagsLoading } = useSiteFlags();
  const { user } = useAuth();
  const isStaff = ["Admin", "SuperAdmin"].includes(user?.role);
  const [input, setInput] = useState("");
  const [chips, setChips] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Load a handful of the most-common public-recipe ingredients so users
  // can one-tap add popular items instead of typing them.
  useEffect(() => {
    if (flagsLoading) return;
    if (!flags.recipe_creator_enabled && !isStaff) return;
    api
      .get("/recipe-creator/suggestions")
      .then((r) => setSuggestions(r.data.suggestions || []))
      .catch(() => setSuggestions([]));
  }, [flags.recipe_creator_enabled, flagsLoading, isStaff]);

  // Auto-match whenever the chip list changes. Debounced (300ms) so rapid
  // add/remove doesn't fire a request per keystroke. Clears results when
  // the last chip is removed. Declared here (before any conditional
  // return) so React hook ordering stays stable.
  useEffect(() => {
    if (flagsLoading) return;
    if (!flags.recipe_creator_enabled && !isStaff) return;
    if (chips.length === 0) {
      setResults(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/recipe-creator/match", { ingredients: chips });
        if (!cancelled) setResults(data);
      } catch {
        if (!cancelled) setResults({ matches: [], count: 0, cleaned: chips, total_scanned: 0, error: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [chips, flags.recipe_creator_enabled, flagsLoading, isStaff]);

  // Feature-flag placeholder for the public before we go live.
  if (!flagsLoading && !flags.recipe_creator_enabled && !isStaff) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center">
        <PageMeta title="Recipe Creator — coming soon" noindex />
        <Sparkle size={56} className="mx-auto text-rf-amber mb-4" weight="duotone" />
        <h1 className="font-display text-4xl font-black text-rf-ink mb-3">Something new is simmering.</h1>
        <p className="text-rf-ink-soft mb-6">
          Recipe Creator matches what you have in your kitchen to public family recipes. It&apos;s almost ready.
        </p>
        <Link to="/directory" className="btn-amber">Browse the directory</Link>
      </div>
    );
  }

  const addChip = (raw) => {
    if (!raw) return;
    // Support pastes like "tomato, garlic, chicken" — split on commas so
    // every chunk becomes its own chip, no matter how the string arrived.
    const parts = String(raw).split(",");
    const next = [...chips];
    for (const p of parts) {
      const clean = p.trim().replace(/^,+|,+$/g, "").toLowerCase();
      if (!clean || clean.length < 2) continue;
      if (next.includes(clean)) continue;
      next.push(clean);
    }
    setChips(next);
    setInput("");
  };

  const removeChip = (c) => setChips(chips.filter((x) => x !== c));

  // Watch for a comma while typing — auto-commit the pre-comma text as a
  // chip so "tomato,garlic" gets chunked in real time.
  const onInputChange = (e) => {
    const v = e.target.value;
    if (v.includes(",")) {
      addChip(v);
    } else {
      setInput(v);
    }
  };

  const onKeyDown = (e) => {
    // Enter/comma/tab commit; backspace on empty removes the last chip.
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      if (input.trim()) {
        e.preventDefault();
        addChip(input);
      }
    } else if (e.key === "Backspace" && input === "" && chips.length > 0) {
      setChips(chips.slice(0, -1));
    }
  };

  const runMatch = () => {
    // Manual run — mostly a fallback for keyboards that don't submit
    // Enter, or the initial add via popular-suggestion chip. The main path
    // is the useEffect auto-run above, so we just make sure any typed-but-
    // uncommitted text lands as a chip.
    if (input.trim()) addChip(input);
  };

  const reset = () => {
    setChips([]);
    setInput("");
    setResults(null);
  };

  const pullFromPantry = async () => {
    // Read shopping-list staples (auto-hidden "always-have" items) and
    // add them to the chip list. Zero-friction way for regular users to
    // start the match with what they know they've got.
    try {
      const { data } = await api.get("/recipe-creator/pantry");
      const items = data?.ingredients || [];
      if (!items.length) {
        toast.info("Nothing pinned to your pantry yet. Pin items from your shopping list.");
        return;
      }
      const next = [...chips];
      let added = 0;
      for (const it of items) {
        const clean = String(it).trim().toLowerCase();
        if (!clean || clean.length < 3 || next.includes(clean)) continue;
        next.push(clean);
        added += 1;
      }
      setChips(next);
      toast.success(added > 0 ? `Added ${added} pantry item${added === 1 ? "" : "s"}` : "Everything from your pantry is already added");
    } catch {
      toast.error("Couldn't load your pantry");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
      <AutoSeo
        pageKey="recipe_creator"
        fallbackTitle="Recipe Creator — Cook with what you have"
        fallbackDescription="Enter the ingredients in your fridge, pantry or cupboard — Recipe Filer instantly matches them to real family recipes from cooks around the world."
      />

      <div className="grid lg:grid-cols-5 gap-8 items-center mb-10">
        <div className="lg:col-span-3">
          <p className="overline mb-4">Recipe Creator</p>
          <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-4 leading-[0.95]">
            What&apos;s in your<br />kitchen?
          </h1>
          <p className="text-lg text-rf-ink-soft max-w-2xl">
            Add the ingredients you already have and we&apos;ll match them to public family recipes — top result is the one you can most nearly make right now.
          </p>
        </div>
        <div className="lg:col-span-2" data-testid="recipe-creator-hero">
          {/* Hero image — open fridge + open pantry. Provided by the site
              owner; hosted from /public so it ships with the SPA build and
              can be aggressively cached by Cloudflare. */}
          <img
            src="/recipe-creator-hero.jpg"
            alt="A tall modern open fridge full of fresh fruit, vegetables and meat, next to an open pantry stacked with jars, cans, sauces and spices — everything Recipe Creator can turn into a meal."
            width={1024}
            height={1024}
            loading="eager"
            fetchPriority="high"
            className="w-full aspect-square rounded-3xl object-cover shadow-2xl border border-rf-ink/5"
          />
        </div>
      </div>

      {isStaff && !flags.recipe_creator_enabled && (
        <div data-testid="recipe-creator-staff-banner" className="mb-8 p-4 rounded-2xl border border-rf-amber bg-rf-amber/15 text-rf-ink text-sm">
          <b>Staff preview:</b> Recipe Creator is currently OFF for the public. Toggle it on in Admin → Site Config when you&apos;re ready to launch.
        </div>
      )}

      {/* Ingredient chip input */}
      <div className="rounded-3xl bg-white border border-rf-ink/10 p-5 md:p-6">
        <div className="flex flex-wrap items-center gap-2" data-testid="recipe-creator-chips">
          {chips.map((c) => (
            <span
              key={c}
              data-testid={`chip-${c}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-rf-amber/30 border border-rf-amber/60 pl-3 pr-1.5 py-1 text-sm font-bold text-rf-ink"
            >
              {c}
              <button
                onClick={() => removeChip(c)}
                data-testid={`chip-remove-${c}`}
                aria-label={`Remove ${c}`}
                className="w-5 h-5 rounded-full bg-white/70 hover:bg-white flex items-center justify-center"
              >
                <X size={12} weight="bold" />
              </button>
            </span>
          ))}
          <input
            data-testid="recipe-creator-input"
            value={input}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            placeholder={chips.length === 0 ? "e.g. chicken, tomato, garlic" : "add another…"}
            className="flex-1 min-w-[180px] px-2 py-1.5 outline-none text-rf-ink placeholder:text-rf-ink-soft"
          />
        </div>

        <div className="flex flex-wrap gap-2 items-center mt-5 pt-5 border-t border-rf-ink/10">
          <p className="text-sm text-rf-ink-soft flex items-center gap-2">
            <MagnifyingGlass size={16} weight="duotone" className="text-rf-orange" />
            {chips.length === 0
              ? "Add ingredients to see matching recipes automatically."
              : loading
                ? "Matching…"
                : results?.count
                  ? `Showing ${results.count} matching ${results.count === 1 ? "recipe" : "recipes"} — updates as you add or remove ingredients.`
                  : "Add another ingredient to widen the search."}
          </p>
          <div className="ml-auto flex flex-wrap gap-2">
            {user && (
              <button
                data-testid="recipe-creator-pull-pantry"
                onClick={pullFromPantry}
                className="btn-outline-ink !py-1.5 !px-3 text-sm inline-flex items-center gap-1.5"
              >
                <PushPin size={14} weight="duotone" /> Pull from my pantry
              </button>
            )}
            {(chips.length > 0 || results) && (
              <button
                data-testid="recipe-creator-reset-btn"
                onClick={reset}
                className="btn-outline-ink !py-1.5 !px-4"
              >
                Start over
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Suggestion chips */}
      {suggestions.length > 0 && chips.length < 3 && (
        <div className="mt-6" data-testid="recipe-creator-suggestions">
          <p className="text-xs uppercase tracking-wider text-rf-ink-soft font-bold mb-3 flex items-center gap-1.5">
            <Basket size={14} weight="duotone" /> Popular ingredients (tap to add)
          </p>
          <div className="flex flex-wrap gap-2">
            {suggestions.filter((s) => !chips.includes(s)).slice(0, 16).map((s) => (
              <button
                key={s}
                onClick={() => addChip(s)}
                data-testid={`suggestion-${s.replace(/\s+/g, "-")}`}
                className="text-sm px-3 py-1.5 rounded-full bg-rf-cream-alt hover:bg-rf-amber/30 text-rf-ink transition-colors border border-transparent hover:border-rf-amber/50"
              >
                + {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {loading && (
        <p className="mt-10 text-rf-ink-soft" data-testid="recipe-creator-loading">
          Sifting through the directory…
        </p>
      )}

      {results && !loading && (
        <div className="mt-12" data-testid="recipe-creator-results">
          {results.count === 0 ? (
            <div className="rounded-3xl bg-white border border-rf-ink/10 p-10 text-center">
              <ForkKnife size={48} className="mx-auto text-rf-amber mb-4" weight="duotone" />
              <p className="font-display text-2xl font-black text-rf-ink mb-2">No matches yet.</p>
              <p className="text-rf-ink-soft mb-6">
                {results.error
                  ? "Something went wrong. Try again in a moment."
                  : "Try adding a few more common ingredients like onion, garlic, egg, or flour."}
              </p>
              <p className="text-xs text-rf-ink-soft">Scanned {results.total_scanned} public recipes.</p>
            </div>
          ) : (
            <>
              <p className="text-rf-ink-soft mb-6">
                Found <b>{results.count}</b> {results.count === 1 ? "recipe" : "recipes"} matching{" "}
                {results.cleaned.map((c, i) => (
                  <span key={c}>
                    <span className="font-bold text-rf-ink">{c}</span>
                    {i < results.cleaned.length - 1 ? ", " : ""}
                  </span>
                ))}
                . Sorted by how many of the recipe&apos;s ingredients you already have.
              </p>

              {results.own_matches && results.own_matches.length > 0 && (
                <div className="mb-10" data-testid="recipe-creator-own-results">
                  <p className="overline mb-3">From your own cookbook{results.own_matches.length === 1 ? "" : "s"}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {results.own_matches.map((r) => (
                      <MatchCard key={r.recipe_id} recipe={r} />
                    ))}
                  </div>
                </div>
              )}

              {results.matches && results.matches.length > 0 && (
                <div data-testid="recipe-creator-public-results">
                  {results.own_matches && results.own_matches.length > 0 && (
                    <p className="overline mb-3">From the community</p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {results.matches.map((r) => (
                      <MatchCard key={r.recipe_id} recipe={r} />
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-rf-ink-soft mt-6 text-center">
                Scanned {results.total_scanned} public recipes to find these matches.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MatchCard({ recipe }) {
  const m = recipe.match || {};
  // Same hero-image priority as the public RecipeCard + RecipeView detail page:
  //   1. AI-generated hero (only when the user has toggled it on)
  //   2. First non-empty user-uploaded image
  // The previous version looked for `cover_image_url` (a Cookbook field, not a
  // Recipe field), so recipes with only an AI image showed the fallback icon.
  const image = (recipe.ai_image_active && recipe.ai_image_url)
    ? recipe.ai_image_url
    : (recipe.images || []).find((u) => typeof u === "string" && u.length > 0);
  const pct = m.match_pct || 0;
  const pctColor = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-rf-amber" : "bg-rf-orange";
  const unused = m.unused_user_tokens || [];
  return (
    <Link
      to={recipePath(recipe)}
      data-testid={`match-card-${recipe.recipe_id}`}
      className="group rounded-3xl bg-white border border-rf-ink/10 overflow-hidden hover:border-rf-orange/60 transition-colors flex flex-col"
    >
      <div className="relative h-40 bg-rf-cream-alt overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={recipe.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ForkKnife size={40} className="text-rf-amber/50" weight="duotone" />
          </div>
        )}
        <div className="absolute top-3 right-3 rounded-full bg-white/95 backdrop-blur pl-1.5 pr-3 py-1 flex items-center gap-2 text-xs font-bold text-rf-ink shadow-sm">
          <span className={`w-6 h-6 rounded-full ${pctColor} text-white flex items-center justify-center text-[10px] font-black`}>
            {pct}%
          </span>
          {m.matched} of {m.total}
        </div>
        {recipe.is_own && (
          <div className="absolute top-3 left-3 rounded-full bg-rf-ink/85 text-rf-cream text-[10px] font-black uppercase tracking-wider px-2 py-1">
            Yours
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <p className="font-display font-black text-rf-ink text-lg leading-tight line-clamp-2">{recipe.title}</p>
        {recipe.creator_name && (
          <p className="text-xs text-rf-ink-soft mt-1">by {recipe.creator_name}</p>
        )}
        {recipe.cook_time_minutes && (
          <p className="text-xs text-rf-ink-soft mt-2 inline-flex items-center gap-1">
            <Clock size={12} weight="duotone" /> {recipe.cook_time_minutes} min
          </p>
        )}
        {m.missing && m.missing.length > 0 && (
          <div className="mt-3 pt-3 border-t border-rf-ink/5">
            <p className="text-[10px] uppercase tracking-wider text-rf-ink-soft font-bold mb-1">
              You&apos;ll also need
            </p>
            <p className="text-xs text-rf-ink-soft line-clamp-2">
              {m.missing.slice(0, 4).join(" · ")}
              {m.missing.length > 4 ? ` · +${m.missing.length - 4} more` : ""}
            </p>
          </div>
        )}
        {unused.length > 0 && (
          <div className="mt-2 pt-2">
            <p className="text-[10px] uppercase tracking-wider text-rf-ink-soft font-bold mb-1 inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
              Not used in this recipe
            </p>
            <p className="text-xs text-orange-700 font-medium">
              {unused.join(" · ")}
            </p>
          </div>
        )}
      </div>
    </Link>
  );
}
